import React, { useState, useEffect } from 'react';
import { Switch } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { AppDispatch, RootState } from '../../../redux/store';
import { updateLoyaltyConfig } from '../store/loyaltySlice';
import { LoyaltyConfig } from '../types';
import notification from '../../../commonComponents/Notification/Notification';
import {
  ConfigCard,
  ConfigHeader,
  ConfigHeaderContent,
  IconWrapper,
  ToggleContainer,
  ToggleContent,
  ToggleInfo,
  ToggleLabel,
  ToggleDescription,
  ConfigGrid,
  ConfigItem,
  ConfigItemLabel,
  ConfigItemDescription,
  ConfigItemExample,
  ConfigIntro,
  ConfigItemValue,
  StyledInput,
  DisabledState,
  DisabledIcon,
  DisabledTitle,
  DisabledDescription,
  ActionButtons,
  PrimaryButton,
  CancelButton,
  HeaderText,
  EditButton,
} from '../styles';

const Configuration: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { config, loading } = useSelector((state: RootState) => state.loyalty);
  const { isDarkMode } = useSelector((state: RootState) => state.theme);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState<LoyaltyConfig>({
    loyaltyEnabled: false,
    loyaltyRedeemRatio: '',
    loyaltySpendRatio: '',
    minSpentForLoyalty: '',
    minSpentForRedemption: '',
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isMerchant = user.role === 'merchant';

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const theme: DefaultTheme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  const handleInputChange = (name: keyof LoyaltyConfig, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(updateLoyaltyConfig(formData)).unwrap();
      setIsEditing(false);
      notification.success('Loyalty configuration updated successfully!');
    } catch (error: any) {
      notification.error(error || 'Failed to save loyalty configuration');
    }
  };

  const handleCancel = () => {
    if (config) {
      setFormData(config);
    }
    setIsEditing(false);
  };

  if (!isMerchant) {
    return null;
  }

  const formatMoney = (value: string) => {
    const num = parseFloat(value);
    if (!value || Number.isNaN(num)) return '$0';
    return `$${num % 1 === 0 ? num : num.toFixed(2)}`;
  };

  const configItems = [
    {
      label: 'Points earned per $1 spent',
      name: 'loyaltySpendRatio' as keyof LoyaltyConfig,
      description:
        'How many points a customer earns for every $1 they spend on an order.',
      example: 'Example: enter 1 → a $20 order gives the customer 20 points.',
      prefix: '',
      suffix: 'pts',
      formatValue: (v: string) => {
        const num = parseFloat(v);
        return v && num > 0 ? `${num} pt${num === 1 ? '' : 's'} per $1` : 'Not set';
      },
      bgColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
      textColor: isDarkMode ? '#3b82f6' : '#2563eb',
    },
    // NOTE: `minSpentForLoyalty` is intentionally hidden for now — the backend
    // (main) does not use it anywhere. The minimum-spend gate for *earning*
    // points currently reuses `minSpentForRedemption`. Re-add an item here once
    // the backend wires `minSpentForLoyalty` into the points-earning logic.
    {
      label: 'Cash value of 1 point',
      name: 'loyaltyRedeemRatio' as keyof LoyaltyConfig,
      description:
        'How much 1 point is worth as a discount when a customer redeems points. Set 0 to turn redemption off.',
      example: 'Example: enter 0.05 → 100 points = $5 off.',
      prefix: '$',
      suffix: '',
      formatValue: (v: string) => {
        const num = parseFloat(v);
        return v && num > 0 ? `${formatMoney(v)} per point` : 'Redemption off';
      },
      bgColor: isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
      borderColor: isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)',
      textColor: isDarkMode ? '#22c55e' : '#16a34a',
    },
    {
      label: 'Minimum order total for points',
      name: 'minSpentForRedemption' as keyof LoyaltyConfig,
      description:
        'The order must be at least this amount for the customer to earn or redeem points. Set 0 for no minimum.',
      example: 'Example: enter 15 → points are only earned/redeemed on orders of $15 or more.',
      prefix: '$',
      suffix: '',
      formatValue: (v: string) => {
        const num = parseFloat(v);
        return v && num > 0 ? `${formatMoney(v)} minimum` : 'No minimum';
      },
      bgColor: isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)',
      borderColor: isDarkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)',
      textColor: isDarkMode ? '#a855f7' : '#9333ea',
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <ConfigCard>
      <ConfigHeader>
        <ConfigHeaderContent>
          <IconWrapper
            $bgColor={isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)'}
            $color={isDarkMode ? '#a855f7' : '#9333ea'}
          >
            <FontAwesomeIcon icon={faGift} style={{ fontSize: '18px' }} />
          </IconWrapper>
          <HeaderText $isDarkMode={isDarkMode}>
            Loyalty Configuration
          </HeaderText>
        </ConfigHeaderContent>
        {!isEditing && (
          <EditButton
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
            type="primary"
          >
            Edit Configuration
          </EditButton>
        )}
      </ConfigHeader>

      <ToggleContainer>
        <ToggleContent>
          <ToggleInfo>
            <ToggleLabel>Enable Loyalty Program</ToggleLabel>
            <ToggleDescription>
              When on, customers collect points on their orders and can redeem
              them for discounts. When off, no points are earned or redeemed.
            </ToggleDescription>
          </ToggleInfo>
          <Switch
            checked={formData.loyaltyEnabled}
            onChange={(checked) => handleInputChange('loyaltyEnabled', checked)}
            disabled={!isEditing}
            style={{ backgroundColor: formData.loyaltyEnabled ? '#52c41a' : undefined }}
          />
        </ToggleContent>
      </ToggleContainer>

      {formData.loyaltyEnabled ? (
        <>
          <ConfigIntro>
            Customers earn points when they spend, then redeem those points for a
            discount on a future order. Set the earning rate and the redemption
            value below.
          </ConfigIntro>

          <ConfigGrid>
            {configItems.map((item) => (
              <ConfigItem key={item.name} $bgColor={item.bgColor} $borderColor={item.borderColor}>
                <ConfigItemLabel>{item.label}</ConfigItemLabel>
                <ConfigItemDescription>{item.description}</ConfigItemDescription>
                {isEditing ? (
                  <>
                    <StyledInput
                      name={item.name}
                      value={formData[item.name] as string}
                      onChange={(e) => handleInputChange(item.name, e.target.value)}
                      placeholder="0"
                      type="number"
                      step="0.01"
                      min="0"
                      prefix={item.prefix || undefined}
                      suffix={item.suffix || undefined}
                    />
                    <ConfigItemExample>{item.example}</ConfigItemExample>
                  </>
                ) : (
                  <ConfigItemValue style={{ color: item.textColor }}>
                    {item.formatValue(formData[item.name] as string)}
                  </ConfigItemValue>
                )}
              </ConfigItem>
            ))}
          </ConfigGrid>

          {isEditing && (
            <ActionButtons>
              <CancelButton onClick={handleCancel} icon={<CloseOutlined />}>
                Cancel
              </CancelButton>
              <PrimaryButton
                onClick={handleSave}
                icon={<SaveOutlined />}
                loading={loading.update}
              >
                Save Changes
              </PrimaryButton>
            </ActionButtons>
          )}
        </>
      ) : (
        <DisabledState>
          <DisabledIcon>
            <FontAwesomeIcon icon={faGift} style={{ fontSize: '32px' }} />
          </DisabledIcon>
          <DisabledTitle>Loyalty program is currently disabled</DisabledTitle>
          <DisabledDescription>
            Enable loyalty to start rewarding your customers
          </DisabledDescription>
        </DisabledState>
      )}
    </ConfigCard>
    </ThemeProvider>
  );
};

export default Configuration;
